import { observable, action, runInAction } from 'mobx';
import { getConfig } from '../apis/ConfigApi';
import _ from 'lodash';

export default class ConfigStore {
  configurationKeys = {
    minPasswordLength: 'minPasswordLength',
    maxPasswordLength: 'maxPasswordLength',
    fleetLoginUrl: 'fleetLoginUrl',
    forgotPasswordUrl: 'forgotPasswordUrl',
    authenticationServiceUrl: 'authenticationServiceUrl',
    fleetAuthenticationUrl: 'fleetAuthenticationUrl',
    privacyPolicyUrl: 'privacyPolicyUrl',
    privacyPolicyUrlUkEu: 'privacyPolicyUrlUkEu',
    staticUrl: 'staticUrl',
    graphQlHubServiceUrl: 'graphQlHubServiceUrl',
    federatedGraphqlHubServiceUrl: 'federatedGraphqlHubServiceUrl',
    googleAnalyticsTrackingId: 'googleAnalyticsTrackingId',
    googleMapsUrl: 'googleMapsUrl',
    googleMapsKey: 'googleMapsKey',
    applicationPermission: 'applicationPermission',
    applicationResourceName: 'applicationResourceName',
    useMockDataInUi: 'useMockDataInUi',
    betaMode: 'betaMode',
    defaultRequestTimeoutInMilliseconds: 'defaultRequestTimeoutInMilliseconds',
    auth0LoginButtonEnabled: 'auth0LoginButtonEnabled',
    auth0Domain: 'auth0Domain',
    auth0ClientID: 'auth0ClientID',
    auth0Audience: 'auth0Audience',
    auth0Nonce: 'auth0Nonce',
    auth0AccessDeniedErrorMessage: 'auth0AccessDeniedErrorMessage',
    idTokenCustomHeader: 'idTokenCustomHeader',
    oidcClientId: 'oidcClientId',
    oidcScope: 'oidcScope',
    oidcRefreshTimeInSec: 'oidcRefreshTimeInSec',
    appLRefreshTimeInSec: 'appLRefreshTimeInSec',
    oidcOffsetTimeInSec: 'oidcOffsetTimeInSec',
    oidcExpiresAt: 'oidcExpiresAt',
    oidcRedirectUri: 'oidcRedirectUri',
    idleStateTimeoutInSec: 'idleStateTimeoutInSec',
    oidcSilentRedirectUri: 'oidcSilentRedirectUri',
    oidcAuthority: 'oidcAuthority',
    oidcAuthEndpoint: 'oidcAuthEndpoint',
    oidcTokenEndpoint: 'oidcTokenEndpoint',
    oidcUserInfoEndpoint: 'oidcUserInfoEndpoint',
    oidcEndSessionEndpoint: 'oidcEndSessionEndpoint',
    oidcRevocationEndpoint: 'oidcRevocationEndpoint',
    oidcIssuer: 'oidcIssuer',
    oidcChangePasswordEndpoint: 'oidcChangePasswordEndpoint',
    oidcResponseType: 'oidcResponseType',
    oidcCodeChallengeMethod: 'oidcCodeChallengeMethod',
    oidcConfigurationName: 'oidcConfigurationName',
    cookieConsentId: 'cookieConsentId',
    cookieConsentClass: 'cookieConsentClass'
  };

  @observable
  config = {
    [this.configurationKeys.minPasswordLength]: 8,
    [this.configurationKeys.maxPasswordLength]: 30,
    [this.configurationKeys.fleetLoginUrl]: undefined,
    [this.configurationKeys.authenticationServiceUrl]: undefined,
    [this.configurationKeys.fleetAuthenticationUrl]: undefined,
    [this.configurationKeys.privacyPolicyUrl]: undefined,
    [this.configurationKeys.privacyPolicyUrlUkEu]: undefined,
    [this.configurationKeys.staticUrl]: undefined,
    [this.configurationKeys.graphQlHubServiceUrl]: undefined,
    [this.configurationKeys.federatedGraphqlHubServiceUrl]: undefined,
    [this.configurationKeys.googleAnalyticsTrackingId]: undefined,
    [this.configurationKeys.googleMapsUrl]: undefined,
    [this.configurationKeys.googleMapsKey]: undefined,
    [this.configurationKeys.applicationPermission]: undefined,
    [this.configurationKeys.applicationResourceName]: undefined,
    [this.configurationKeys.useMockDataInUi]: false,
    [this.configurationKeys.standardizedTimezone]: undefined,
    [this.configurationKeys.defaultRequestTimeoutInMilliseconds]: 10000,
    [this.configurationKeys.auth0LoginButtonEnabled]: false,
    [this.configurationKeys.auth0Domain]: undefined,
    [this.configurationKeys.auth0ClientID]: undefined,
    [this.configurationKeys.auth0Audience]: undefined,
    [this.configurationKeys.auth0Nonce]: undefined,
    [this.configurationKeys.auth0AccessDeniedErrorMessage]: undefined,
    [this.configurationKeys.oidcClientId]: undefined,
    [this.configurationKeys.oidcScope]: undefined,
    [this.configurationKeys.oidcRefreshTimeInSec]: undefined,
    [this.configurationKeys.appLRefreshTimeInSec]: undefined,
    [this.configurationKeys.oidcExpiresAt]: undefined,
    [this.configurationKeys.oidcOffsetTimeInSec]: undefined,
    [this.configurationKeys.oidcRedirectUri]: undefined,
    [this.configurationKeys.idleStateTimeoutInSec]: undefined,
    [this.configurationKeys.oidcSilentRedirectUri]: undefined,
    [this.configurationKeys.oidcAuthority]: undefined,
    [this.configurationKeys.oidcAuthEndpoint]: undefined,
    [this.configurationKeys.oidcTokenEndpoint]: undefined,
    [this.configurationKeys.oidcUserInfoEndpoint]: undefined,
    [this.configurationKeys.oidcEndSessionEndpoint]: undefined,
    [this.configurationKeys.oidcRevocationEndpoint]: undefined,
    [this.configurationKeys.oidcIssuer]: undefined,
    [this.configurationKeys.oidcChangePasswordEndpoint]: undefined,
    [this.configurationKeys.oidcResponseType]: undefined,
    [this.configurationKeys.oidcCodeChallengeMethod]: undefined,
    [this.configurationKeys.oidcConfigurationName]: undefined,
    [this.configurationKeys.idTokenCustomHeader]: 'Id-JWT-Token',
    [this.configurationKeys.cookieConsentId]: undefined,
    [this.configurationKeys.cookieConsentClass]: undefined
  };

  @observable configIsLoaded = false;

  @observable
  loading = {
    config: false
  };

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  async loadConfigAsync() {
    try {
      const response = await getConfig();
      return response;
    } catch (error) {
      throw new Error('Config could not be loaded.');
    }
  }

  /**
   * MobX
   *
   * Actions
   */
  @action('Init config store')
  async init() {
    if (!this.configIsLoaded) {
      await this.loadConfigIntoStore();
    }
  }

  @action('Load config from async file')
  async loadConfigIntoStore() {
    this.setConfigIsLoading(true);

    try {
      const response = await this.loadConfigAsync();

      runInAction(() => {
        _.extend(this.config, response.data);
        this.setConfigIsLoading(false);
        this.configIsLoaded = true;
      });
    } catch (error) {
      console.error('Could not load config from remote file.');
    }
  }

  @action('Set config loading state')
  setConfigIsLoading(isLoading) {
    this.loading.config = isLoading;
  }

  /**
   * This function should be used to access the config object in situations where config may not have been loaded yet.
   * Think of it this way: use the @observable config object if you need to render the config values in react, use getConfig and await
   * to get the config object with certainty that if it can be loaded it will be loaded.
   *
   * Side effects: if the config is loaded here it will update the config object on the store.
   */
  @action('Get config')
  async getConfig() {
    if (this.configIsLoaded) {
      return this.config;
    }

    await this.loadConfigIntoStore();
    return this.config;
  }
}



// WEBPACK FOOTER //
// ./src/stores/ConfigStore.js